import { Button } from 'components'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { copyToClip } from 'utils'

import { RefreshIcon, UserGroupIcon } from '@heroicons/react/outline'
import { CopyIcon } from '@radix-ui/react-icons'
import { tx } from '@twind/core'
import { currentURL } from 'config/api'

import { useActiveUsers, useJoinRoom } from '../CodingRoom.services'
import { RoomData } from '../codingRooms.types'
import { formatTime } from '../utils'

const CodingRoomCard = ({
  room,
  isCurrent
}: {
  room: RoomData
  isCurrent: boolean
}) => {
  const router = useRouter()
  const { mutateAsync: joinRoom } = useJoinRoom()
  const { data: usersCount, refetch } = useActiveUsers(room.id)

  const lastFetchTimeRef = useRef<number>(0)
  const [roomStatus, setRoomStatus] = useState<
    'scheduled' | 'running' | 'ended' | null
  >(null)

  useEffect(() => {
    const now = new Date()
    const startTime = new Date(room.starts_at)
    const endTime = new Date(room.finish_at)

    if (now < startTime) {
      setRoomStatus('scheduled')
    } else if (now >= startTime && now < endTime) {
      setRoomStatus('running')
    } else {
      setRoomStatus('ended')
    }
  }, [room])

  const joinCodingRoom = useCallback(
    async (uniqueRoomCode: string) => {
      try {
        const { coding_room_id: room_id } = await joinRoom(uniqueRoomCode)
        router.push(`/coding-rooms/${room_id}`)
      } catch (e: any) {
        if (e.response?.data) {
          toast.error(e.response?.data.data.attributes.error.message)
        } else {
          toast.error('something went wrong')
        }
      }
    },
    [joinRoom, router]
  )

  const fetchActiveUsers = useCallback(async () => {
    const now = Date.now()

    // Throttle for 1 second
    if (now - lastFetchTimeRef.current > 1000) {
      lastFetchTimeRef.current = now
      await refetch()
    }
  }, [refetch])

  return (
    <div className="bg-[#1D1D20] py-4 space-y-4">
      <p
        className={tx(
          roomStatus === 'running' && 'text-green-500',
          roomStatus === 'ended' && 'text-red-500',
          roomStatus === 'scheduled' && 'text-white',
          'px-4 py-2 max-w-max ml-auto mr-3 rounded-full text-sm font-bold bg-[#070707]'
        )}>
        {roomStatus === 'scheduled'
          ? 'Not Started'
          : roomStatus === 'running'
          ? 'Started'
          : 'Ended'}
      </p>

      <div className="flex flex-col mt-3 gap-y-4 px-6">
        <h2 className="text-lg font-semibold">{room.name}</h2>

        <div className="flex flex-col space-y-4 flex-wrap max-w-full">
          <div className="flex space-x-3 items-center">
            <p className="text-zinc-400">Room Start Time:</p>
            <p className="text-primary-light">{formatTime(room.starts_at)}</p>
          </div>

          <div className="flex space-x-3 items-center">
            <p className="text-zinc-400">Room Time:</p>
            <p>{parseInt(room.room_time) / 60} min</p>
          </div>

          <div className="flex space-x-3 items-center">
            <p className="text-zinc-400">Room End Time:</p>
            <p>{formatTime(room.finish_at)}</p>
          </div>

          <div className="flex items-center flex-wrap max-w-full">
            <p className="text-zinc-400 pr-3">Topics:</p>
            {room.topics && (
              <span className="capitalize pl-1">
                {JSON.parse(room.topics).join(', ')}
              </span>
            )}
          </div>

          <div className="flex space-x-3 items-center">
            <p className="text-zinc-400">Number of questions:</p>
            <p>{String(room.question_count).padStart(2, '0')}</p>
          </div>

          <div className="flex items-center">
            <p className="text-zinc-400 pr-3">Difficulty:</p>
            {room.difficulty && (
              <span className="capitalize pl-1">
                {JSON.parse(room.difficulty).join(', ')}
              </span>
            )}
          </div>
        </div>

        <div className="w-full">
          {isCurrent ? (
            <div className="flex flex-col xl:flex-row xl:items-center gap-4">
              <Button
                className="bg-[#242428] px-3 py-2 text-base text-white rounded-[5px] flex items-center justify-center gap-x-2 hover:bg-[#242428] focus:ring-indigo-500 focus:border-indigo-500 border-transparent"
                variant="transparent"
                onClick={() => {
                  copyToClip(
                    `${currentURL}/coding-rooms/join?unique_room_code=${room.unique_id}`,
                    true
                  )
                }}>
                <span>Invite Friends</span>
                <CopyIcon className="w-5 h-5" />
              </Button>

              <Button
                className="xl:ml-auto disabled:cursor-not-allowed cursor-pointer px-3 py-2 flex items-center justify-center gap-x-2"
                onClick={() => router.push(`/coding-rooms/${room.id}`)}>
                <span>Go To Room</span>
              </Button>
            </div>
          ) : (
            <Button
              className="xl:ml-auto disabled:cursor-not-allowed cursor-pointer px-3 py-2 flex items-center justify-center gap-x-2"
              onClick={() => joinCodingRoom(room.unique_id)}>
              <span>Join Room</span>
            </Button>
          )}
        </div>
      </div>

      <hr className="h-px bg-elevation-5 w-full outline-none border-none" />

      <div className="flex items-center justify-between px-6">
        <div className="flex items-center justify-start gap-x-2">
          <UserGroupIcon className="w-5 h-5" />
          <p className="text-lg">People in room</p>

          <button className="focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 border-transparent focus:outline-none">
            <RefreshIcon
              className="w-5 h-5 text-primary"
              onClick={fetchActiveUsers}
            />
          </button>
        </div>

        <p className="bg-elevation-5 h-10 w-10 flex items-center justify-center rounded">
          {usersCount || '-'}
        </p>
      </div>
    </div>
  )
}

export default CodingRoomCard
